import React, { useState, useEffect } from 'react';
import { planAPI } from '../../../services/api';
import { PlusIcon, PencilIcon, TrashIcon, CheckCircleIcon, XCircleIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import SearchableDropdown from '../../../components/common/SearchableDropdown';
import Pagination from '../../../components/common/Pagination';
import UniversalSearchInput from '../../../components/common/UniversalSearchInput';

const Plans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showSMPPMappingModal, setShowSMPPMappingModal] = useState(false);
  const [showRouteMappingModal, setShowRouteMappingModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [mappingPlan, setMappingPlan] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10); // PROMPT-112: Default pagination = 10 rows
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [smppConnectors, setSmppConnectors] = useState([]);
  const [messageRoutes, setMessageRoutes] = useState([]);
  const [smppMappings, setSmppMappings] = useState([]);
  const [routeMappings, setRouteMappings] = useState([]);
  const [smppFormData, setSmppFormData] = useState({
    smpp_connector_id: '',
    priority: 0,
    is_active: true
  });
  const [routeFormData, setRouteFormData] = useState({
    route_id: '',
    route_category: 'DEFAULT',
    priority: 0,
    is_active: true
  });
  const [formData, setFormData] = useState({
    plan_name: '',
    description: '',
    plan_type: 'HTTP',
    plan_category: 'DLT',
    plan_channel: 'HTTP',
    price: 0,
    currency: 'USD',
    sms_limit: 0,
    validity_days: 30,
    features: '',
    http_api_enabled: true,
    smpp_enabled: false,
    default_tps: 10,
    rate_per_sms: 0,
    is_active: true
  });

  useEffect(() => {
    fetchPlans();
    fetchSMPPConnectors();
    fetchMessageRoutes();
  }, []);

  // PROMPT-098: Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
    fetchPlans(1, pageSize);
  }, [searchTerm, filterStatus]);

  // PROMPT-098: Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchPlans(page, pageSize);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
    fetchPlans(1, newPageSize);
  };

  const fetchPlans = async (page = currentPage, size = pageSize) => {
    try {
      setLoading(true);
      const response = await planAPI.getAll(page, size);
      
      // PROMPT-130: Handle response format { success: true, plans: [...] }
      let plansData = [];
      let total = 0;
      
      if (response.data?.plans && Array.isArray(response.data.plans)) {
        // Format: { data: { success: true, plans: [...] } }
        plansData = response.data.plans;
        total = response.data.total || plansData.length;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        // Format with pagination: { data: { data: [...], total: 356, page: 1, page_size: 20 } }
        plansData = response.data.data;
        total = response.data.total || 0;
      } else if (Array.isArray(response.data)) {
        // Direct array format
        plansData = response.data;
        total = plansData.length;
      }
      
      setPlans(plansData);
      setTotalRecords(total);
      setTotalPages(Math.ceil(total / size));
    } catch (error) {
      console.error('Failed to fetch plans:', error);
      setError('Failed to fetch plans');
      setPlans([]);
      setTotalRecords(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const fetchSMPPConnectors = async () => {
    try {
      const response = await planAPI.getSMPPConnectors();
      setSmppConnectors(response.data.connectors || []);
    } catch (error) {
      console.error('Failed to fetch SMPP connectors:', error);
      setSmppConnectors([]);
    }
  };

  const fetchMessageRoutes = async () => {
    try {
      const response = await planAPI.getMessageRoutes();
      setMessageRoutes(response.data || []);
    } catch (error) {
      console.error('Failed to fetch message routes:', error);
    }
  };

  const fetchSMPPMappings = async (planId) => {
    try {
      const response = await planAPI.getSMPPMappings(planId);
      setSmppMappings(response.data || []);
    } catch (error) {
      console.error('Failed to fetch SMPP mappings:', error);
    }
  };

  const fetchRouteMappings = async (planId) => {
    try {
      const response = await planAPI.getRouteMappings(planId);
      setRouteMappings(response.data || []);
    } catch (error) {
      console.error('Failed to fetch route mappings:', error);
    }
  };

  const handleOpenSMPPMappingModal = async (plan) => {
    setMappingPlan(plan);
    await fetchSMPPMappings(plan.id);
    setSmppFormData({
      smpp_connector_id: '',
      priority: 0,
      is_active: true
    });
    setShowSMPPMappingModal(true);
  };

  const handleOpenRouteMappingModal = async (plan) => {
    setMappingPlan(plan);
    await fetchRouteMappings(plan.id);
    setRouteFormData({
      route_id: '',
      route_category: 'DEFAULT',
      priority: 0,
      is_active: true
    });
    setShowRouteMappingModal(true);
  };

  const handleSMPPMappingSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = {
        ...smppFormData,
        smpp_connector_id: parseInt(smppFormData.smpp_connector_id)
      };
      await planAPI.createSMPPMapping(mappingPlan.id, dataToSend);
      setSuccess('SMPP connector mapped successfully');
      setShowSMPPMappingModal(false);
      await fetchSMPPMappings(mappingPlan.id);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to map SMPP connector');
    }
  };

  const handleRouteMappingSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = {
        ...routeFormData,
        route_id: parseInt(routeFormData.route_id)
      };
      await planAPI.createRouteMapping(mappingPlan.id, dataToSend);
      setSuccess('Route mapped successfully');
      setShowRouteMappingModal(false);
      await fetchRouteMappings(mappingPlan.id);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to map route');
    }
  };

  const handleDeleteSMPPMapping = async (mappingId) => {
    if (window.confirm('Are you sure you want to remove this SMPP mapping?')) {
      try {
        await planAPI.deleteSMPPMapping(mappingId);
        setSuccess('SMPP mapping removed successfully');
        await fetchSMPPMappings(mappingPlan.id);
      } catch (error) {
        setError(error.response?.data?.error || 'Failed to remove SMPP mapping');
      }
    }
  };

  const handleDeleteRouteMapping = async (mappingId) => {
    if (window.confirm('Are you sure you want to remove this route mapping?')) {
      try {
        await planAPI.deleteRouteMapping(mappingId);
        setSuccess('Route mapping removed successfully');
        await fetchRouteMappings(mappingPlan.id);
      } catch (error) {
        setError(error.response?.data?.error || 'Failed to remove route mapping');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPlan) {
        await planAPI.update(editingPlan.id, formData);
        setSuccess('Plan updated successfully');
      } else {
        await planAPI.create(formData);
        setSuccess('Plan created successfully');
      }
      setShowModal(false);
      resetForm();
      fetchPlans();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to save plan');
    }
  };

  const handleEdit = (plan) => {
    setEditingPlan(plan);
    setFormData({
      plan_name: plan.plan_name,
      description: plan.description || '',
      plan_type: plan.plan_type || 'HTTP',
      plan_category: plan.plan_category || 'DLT',
      plan_channel: plan.plan_channel || 'HTTP',
      price: plan.price,
      currency: plan.currency,
      sms_limit: plan.sms_limit,
      validity_days: plan.validity_days,
      features: plan.features || '',
      http_api_enabled: plan.http_api_enabled || false,
      smpp_enabled: plan.smpp_enabled || false,
      default_tps: plan.default_tps || 10,
      rate_per_sms: plan.rate_per_sms || 0,
      is_active: plan.is_active
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this plan?')) {
      try {
        await planAPI.delete(id);
        setSuccess('Plan deleted successfully');
        fetchPlans();
      } catch (error) {
        setError(error.response?.data?.error || 'Failed to delete plan');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      plan_name: '',
      description: '',
      plan_type: 'HTTP',
      plan_category: 'DLT',
      plan_channel: 'HTTP',
      price: 0,
      currency: 'USD',
      sms_limit: 0,
      validity_days: 30,
      features: '',
      http_api_enabled: true,
      smpp_enabled: false,
      default_tps: 10,
      rate_per_sms: 0,
      is_active: true
    });
    setEditingPlan(null);
    setError('');
  };

  const filteredPlans = plans.filter(plan => {
    const matchesSearch = plan.plan_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (plan.description && plan.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = filterStatus === 'all' || 
                         (filterStatus === 'active' && plan.is_active) ||
                         (filterStatus === 'inactive' && !plan.is_active);
    return matchesSearch && matchesStatus;
  });

  const formatFeatures = (features) => {
    try {
      return JSON.stringify(JSON.parse(features), null, 2);
    } catch {
      return features;
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-md p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Plan Management</h1>
        <button
          onClick={() => { setShowModal(true); resetForm(); }}
          className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700"
        >
          <PlusIcon className="w-5 h-5" />
          Add Plan
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4">
          {success}
        </div>
      )}

      <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <UniversalSearchInput
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search plans..."
            className="ml-2"
          />
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="block w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>
      <div className="mb-4 text-sm text-gray-600">
        Showing {filteredPlans.length} of {plans.length} plans
      </div>

      {loading ? (
        <div className="text-center py-8">Loading...</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Plan Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Channel
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rate/SMS
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  SMS Limit
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Validity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  TPS
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredPlans.map((plan) => (
                <tr key={plan.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{plan.plan_name}</div>
                      {plan.description && (
                        <div className="text-sm text-gray-500 truncate max-w-xs">{plan.description}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      plan.plan_category === 'INTERNATIONAL' 
                        ? 'bg-orange-100 text-orange-800' 
                        : 'bg-indigo-100 text-indigo-800'
                    }`}>
                      {plan.plan_category || 'DLT'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      plan.plan_channel === 'HTTP' 
                        ? 'bg-blue-100 text-blue-800' 
                        : plan.plan_channel === 'SMPP'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-purple-100 text-purple-800'
                    }`}>
                      {plan.plan_channel}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{plan.currency} {parseFloat(plan.price).toFixed(6).replace(/\.?0+$/, '')}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {plan.rate_per_sms ? `${plan.currency} ${parseFloat(plan.rate_per_sms).toFixed(6).replace(/\.?0+$/, '')}` : '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {plan.sms_limit === 0 ? 'Unlimited' : plan.sms_limit.toLocaleString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {plan.validity_days === 0 ? 'Lifetime' : `${plan.validity_days} days`}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {plan.default_tps || 10}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      plan.is_active 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {plan.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(plan)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Edit Plan"
                      >
                        <PencilIcon className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleOpenSMPPMappingModal(plan)}
                        className="text-green-600 hover:text-green-900"
                        title="SMPP Account Mapping"
                      >
                        <PlusIcon className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleOpenRouteMappingModal(plan)}
                        className="text-purple-600 hover:text-purple-900"
                        title="Routing Management"
                      >
                        <MagnifyingGlassIcon className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(plan.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete Plan"
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {/* PROMPT-098: Pagination Footer */}
          <Pagination
            currentPage={currentPage}
            pageSize={pageSize}
            totalRecords={totalRecords}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={() => setShowModal(false)}></div>

            {/* Modal */}
            <div className="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <form onSubmit={handleSubmit}>
                {/* Header */}
                <div className="bg-white px-4 sm:px-6 pt-5 pb-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-bold text-gray-900">
                      {editingPlan ? 'Edit Plan' : 'Add New Plan'}
                    </h3>
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="text-gray-400 hover:text-gray-500 transition-colors"
                    >
                      <span className="text-2xl">&times;</span>
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="bg-white px-4 sm:px-6 py-5 max-h-[calc(100vh-200px)] overflow-y-auto">
                  <div className="space-y-5">
                    {/* Plan Name */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Plan Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.plan_name}
                        onChange={(e) => setFormData({...formData, plan_name: e.target.value})}
                        className="w-full px-3 sm:px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent text-sm min-h-[40px]"
                        placeholder="Enter plan name"
                      />
                    </div>

                    {/* Description */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Description
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                        rows={3}
                        className="w-full px-3 sm:px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent text-sm resize-none"
                        placeholder="Enter plan description"
                      />
                    </div>

                    {/* Plan Category */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Plan Category *
                      </label>
                      <SearchableDropdown
                        options={[
                          { value: 'DLT', label: 'DLT (Domestic - Full DLT Validation)' },
                          { value: 'INTERNATIONAL', label: 'International (No DLT Validation)' }
                        ]}
                        value={formData.plan_category}
                        onChange={(value) => setFormData({...formData, plan_category: value})}
                        placeholder="Select Plan Category"
                        clearable={false}
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        {formData.plan_category === 'INTERNATIONAL' 
                          ? 'International: Header optional, Template ID & DLT Entity ID not validated against database'
                          : 'DLT: Full PE-TM chain validation, Template ID & Entity ID validated against database'}
                      </p>
                    </div>

                    {/* Plan Type & Plan Channel */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Plan Type *
                        </label>
                        <SearchableDropdown
                          options={[
                            { value: 'HTTP', label: 'HTTP API' },
                            { value: 'SMPP', label: 'SMPP' },
                            { value: 'BOTH', label: 'Both HTTP & SMPP' }
                          ]}
                          value={formData.plan_type}
                          onChange={(value) => {
                            const newFormData = {...formData, plan_type: value};
                            // Auto-set plan_channel and capabilities based on plan_type
                            if (value === 'HTTP') {
                              newFormData.plan_channel = 'HTTP';
                              newFormData.http_api_enabled = true;
                              newFormData.smpp_enabled = false;
                            } else if (value === 'SMPP') {
                              newFormData.plan_channel = 'SMPP';
                              newFormData.http_api_enabled = false;
                              newFormData.smpp_enabled = true;
                            } else if (value === 'BOTH') {
                              newFormData.plan_channel = 'BOTH';
                              newFormData.http_api_enabled = true;
                              newFormData.smpp_enabled = true;
                            }
                            setFormData(newFormData);
                          }}
                          placeholder="Select Plan Type"
                          clearable={false}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Plan Channel *
                        </label>
                        <SearchableDropdown
                          options={[
                            { value: 'HTTP', label: 'HTTP Channel' },
                            { value: 'SMPP', label: 'SMPP Channel' },
                            { value: 'BOTH', label: 'Both Channels' }
                          ]}
                          value={formData.plan_channel}
                          onChange={(value) => {
                            const newFormData = {...formData, plan_channel: value};
                            // Auto-adjust capabilities based on channel
                            if (value === 'HTTP') {
                              newFormData.http_api_enabled = true;
                              newFormData.smpp_enabled = false;
                            } else if (value === 'SMPP') {
                              newFormData.http_api_enabled = false;
                              newFormData.smpp_enabled = true;
                            } else if (value === 'BOTH') {
                              newFormData.http_api_enabled = true;
                              newFormData.smpp_enabled = true;
                            }
                            setFormData(newFormData);
                          }}
                          placeholder="Select Channel"
                          clearable={false}
                        />
                      </div>
                    </div>

                    {/* Price & Currency */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Price *
                        </label>
                        <input
                          type="number"
                          step="0.000001"
                          min="0"
                          required
                          value={formData.price}
                          onChange={(e) => setFormData({...formData, price: parseFloat(e.target.value)})}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent text-sm min-h-[40px]"
                          placeholder="0.000000"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Currency *
                        </label>
                        <SearchableDropdown
                          options={[
                            { value: 'USD', label: 'USD - US Dollar' },
                            { value: 'EUR', label: 'EUR - Euro' },
                            { value: 'GBP', label: 'GBP - British Pound' },
                            { value: 'INR', label: 'INR - Indian Rupee' }
                          ]}
                          value={formData.currency}
                          onChange={(value) => setFormData({...formData, currency: value})}
                          placeholder="Select Currency"
                          clearable={false}
                        />
                      </div>
                    </div>

                    {/* SMS Limit & Validity Days */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          SMS Limit *
                        </label>
                        <input
                          type="number"
                          min="0"
                          required
                          value={formData.sms_limit}
                          onChange={(e) => setFormData({...formData, sms_limit: parseInt(e.target.value)})}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent text-sm min-h-[40px]"
                          placeholder="0"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Validity Days *
                        </label>
                        <input
                          type="number"
                          min="0"
                          required
                          value={formData.validity_days}
                          onChange={(e) => setFormData({...formData, validity_days: parseInt(e.target.value)})}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent text-sm min-h-[40px]"
                          placeholder="30"
                        />
                      </div>
                    </div>

                    {/* Rate per SMS & Default TPS */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Rate per SMS
                        </label>
                        <input
                          type="number"
                          step="0.000001"
                          min="0"
                          value={formData.rate_per_sms}
                          onChange={(e) => setFormData({...formData, rate_per_sms: parseFloat(e.target.value)})}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent text-sm min-h-[40px]"
                          placeholder="0.000000"
                        />
                        <p className="mt-1 text-xs text-gray-500">Optional: Rate per SMS in selected currency</p>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Default TPS
                        </label>
                        <input
                          type="number"
                          min="1"
                          value={formData.default_tps}
                          onChange={(e) => setFormData({...formData, default_tps: parseInt(e.target.value)})}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent text-sm min-h-[40px]"
                          placeholder="10"
                        />
                        <p className="mt-1 text-xs text-gray-500">Transactions per second limit</p>
                      </div>
                    </div>

                    {/* Features JSON */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Features (JSON Format)
                      </label>
                      <textarea
                        value={formData.features}
                        onChange={(e) => setFormData({...formData, features: e.target.value})}
                        rows={4}
                        placeholder='{"support": "email", "priority": "normal"}'
                        className="w-full px-3 sm:px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent text-xs font-mono resize-none"
                      />
                      <p className="mt-1 text-xs text-gray-500">Optional: Enter plan features in JSON format</p>
                    </div>

                    {/* Active Checkbox */}
                    <div className="flex items-center p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                      <input
                        type="checkbox"
                        id="is_active"
                        checked={formData.is_active}
                        onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label htmlFor="is_active" className="ml-3 block text-sm font-medium text-gray-900">
                        Set plan as active
                      </label>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="bg-gray-50 px-4 sm:px-6 py-3 sm:flex sm:flex-row-reverse gap-3 border-t border-gray-200">
                  <button
                    type="submit"
                    className="w-full sm:w-auto inline-flex justify-center items-center px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors min-h-[40px]"
                  >
                    {editingPlan ? 'Update Plan' : 'Create Plan'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="w-full sm:w-auto mt-3 sm:mt-0 inline-flex justify-center items-center px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors min-h-[40px]"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* SMPP Mapping Modal */}
      {showSMPPMappingModal && mappingPlan && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" onClick={() => setShowSMPPMappingModal(false)}>
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <form onSubmit={handleSMPPMappingSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="mb-4">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      SMPP Account Mapping for Plan: {mappingPlan.plan_name}
                    </h3>
                  </div>

                  {/* Current SMPP Mappings */}
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Current SMPP Mappings</h4>
                    {smppMappings.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Connector</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Operator</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Host:Port</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {smppMappings.map((mapping) => (
                              <tr key={mapping.id}>
                                <td className="px-4 py-2 text-sm">{mapping.connector?.connector_name}</td>
                                <td className="px-4 py-2 text-sm">{mapping.connector?.operator_name}</td>
                                <td className="px-4 py-2 text-sm">{mapping.connector?.host}:{mapping.connector?.port}</td>
                                <td className="px-4 py-2 text-sm">{mapping.priority}</td>
                                <td className="px-4 py-2">
                                  <span className={`inline-flex px-2 py-1 text-xs rounded ${
                                    mapping.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                  }`}>
                                    {mapping.is_active ? 'Active' : 'Inactive'}
                                  </span>
                                </td>
                                <td className="px-4 py-2">
                                  <button
                                    onClick={() => handleDeleteSMPPMapping(mapping.id)}
                                    className="text-red-600 hover:text-red-900"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No SMPP connectors mapped</p>
                    )}
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Select SMPP Connector</label>
                      <select
                        required
                        value={smppFormData.smpp_connector_id}
                        onChange={(e) => setSmppFormData({...smppFormData, smpp_connector_id: e.target.value})}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      >
                        <option value="">Choose a SMPP connector...</option>
                        {smppConnectors.filter(c => c.is_active).map((connector) => (
                          <option key={connector.id} value={connector.id}>
                            {connector.connector_name} - {connector.operator_name} ({connector.host}:{connector.port})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Priority</label>
                        <input
                          type="number"
                          min="0"
                          value={smppFormData.priority}
                          onChange={(e) => setSmppFormData({...smppFormData, priority: parseInt(e.target.value)})}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                      </div>

                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="smpp_is_active"
                          checked={smppFormData.is_active}
                          onChange={(e) => setSmppFormData({...smppFormData, is_active: e.target.checked})}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <label htmlFor="smpp_is_active" className="ml-2 block text-sm text-gray-900">
                          Active
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Add SMPP Mapping
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowSMPPMappingModal(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Route Mapping Modal */}
      {showRouteMappingModal && mappingPlan && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" onClick={() => setShowRouteMappingModal(false)}>
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <form onSubmit={handleRouteMappingSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="mb-4">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Routing Management for Plan: {mappingPlan.plan_name}
                    </h3>
                  </div>

                  {/* Current Route Mappings */}
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Current Route Mappings</h4>
                    {routeMappings.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Route Name</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Route Type</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Connector</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Operator Circle</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Number Series</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">DLT</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {routeMappings.map((mapping) => (
                              <tr key={mapping.id} className="hover:bg-gray-50">
                                <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                  {mapping.route?.route_name}
                                  <div className="text-xs text-gray-500">ID: {mapping.route_id}</div>
                                </td>
                                <td className="px-4 py-3">
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                    mapping.route_category === 'DEFAULT' ? 'bg-blue-100 text-blue-800' :
                                    mapping.route_category === 'PROMOTIONAL' ? 'bg-purple-100 text-purple-800' :
                                    mapping.route_category === 'TRANSACTIONAL' ? 'bg-green-100 text-green-800' :
                                    'bg-orange-100 text-orange-800'
                                  }`}>
                                    {mapping.route_category}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-sm">
                                  <span className={`inline-flex px-2 py-1 text-xs rounded ${
                                    mapping.route?.route_type === 'PRIMARY' ? 'bg-indigo-100 text-indigo-800' :
                                    mapping.route?.route_type === 'BACKUP' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {mapping.route?.route_type}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-sm">
                                  {mapping.route?.connector ? (
                                    <div>
                                      <div className="font-medium text-gray-900">{mapping.route.connector.connector_name}</div>
                                      <div className="text-xs text-gray-500">{mapping.route.connector.operator_name}</div>
                                      <div className="text-xs text-gray-400">{mapping.route.connector.host}:{mapping.route.connector.port}</div>
                                    </div>
                                  ) : (
                                    <span className="text-gray-400">No connector</span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900">
                                  {mapping.route?.operator_circle || '-'}
                                </td>
                                <td className="px-4 py-3 text-sm">
                                  {mapping.route?.number_series_prefix ? (
                                    <span className="inline-flex px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded">
                                      {mapping.route.number_series_prefix}
                                    </span>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-sm">
                                  {mapping.route?.dlt_required ? (
                                    <span className="inline-flex px-2 py-1 text-xs bg-red-100 text-red-800 rounded font-medium">
                                      Required
                                    </span>
                                  ) : (
                                    <span className="inline-flex px-2 py-1 text-xs bg-green-100 text-green-800 rounded">
                                      Not Required
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-sm">
                                  <div className="flex items-center space-x-2">
                                    <span className="text-gray-900">Map: {mapping.priority}</span>
                                    {mapping.route?.priority !== mapping.priority && (
                                      <span className="text-xs text-gray-500">(Route: {mapping.route.priority})</span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-3">
                                  <div className="flex flex-col space-y-1">
                                    <span className={`inline-flex px-2 py-1 text-xs rounded ${
                                      mapping.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                    }`}>
                                      Mapping: {mapping.is_active ? 'Active' : 'Inactive'}
                                    </span>
                                    {!mapping.route?.is_active && (
                                      <span className="inline-flex px-2 py-1 text-xs rounded bg-gray-100 text-gray-600">
                                        Route: Inactive
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-3">
                                  <button
                                    onClick={() => handleDeleteRouteMapping(mapping.id)}
                                    className="text-red-600 hover:text-red-900 transition-colors"
                                    title="Remove route mapping"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <div className="text-gray-400 mb-2">
                          <MagnifyingGlassIcon className="h-12 w-12 mx-auto" />
                        </div>
                        <p className="text-sm text-gray-500">No routes mapped to this plan</p>
                        <p className="text-xs text-gray-400 mt-1">Add routes to enable SMS routing for this plan</p>
                      </div>
                    )}
                  </div>

                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Select Route</label>
                        <select
                          required
                          value={routeFormData.route_id}
                          onChange={(e) => setRouteFormData({...routeFormData, route_id: e.target.value})}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        >
                          <option value="">Choose a route...</option>
                          {messageRoutes.filter(r => r.is_active).map((route) => (
                            <option key={route.id} value={route.id}>
                              {route.route_name} ({route.route_type})
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">Route Category</label>
                        <select
                          required
                          value={routeFormData.route_category}
                          onChange={(e) => setRouteFormData({...routeFormData, route_category: e.target.value})}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        >
                          <option value="DEFAULT">Default Route</option>
                          <option value="PROMOTIONAL">Promotional Route</option>
                          <option value="TRANSACTIONAL">Transactional Route</option>
                          <option value="INTERNATIONAL">International Route</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Priority</label>
                        <input
                          type="number"
                          min="0"
                          value={routeFormData.priority}
                          onChange={(e) => setRouteFormData({...routeFormData, priority: parseInt(e.target.value)})}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                      </div>

                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="route_is_active"
                          checked={routeFormData.is_active}
                          onChange={(e) => setRouteFormData({...routeFormData, is_active: e.target.checked})}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <label htmlFor="route_is_active" className="ml-2 block text-sm text-gray-900">
                          Active
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-purple-600 text-base font-medium text-white hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Add Route Mapping
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowRouteMappingModal(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Plans;
